import { useState } from "react";
import { CheckCircle2, Signpost, Home as HomeIcon, Phone, Footprints, Mic, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Chapter } from "@/components/Chapter";
import { Reveal } from "@/components/Reveal";
import StripeBar from "@/components/StripeBar";
import { postForm } from "@/lib/api";
import { EVENTS } from "@/lib/data";

const OPTIONS = [
  { id: "yard-sign", label: "Request a Yard Sign", icon: Signpost },
  { id: "host-event", label: "Host an Event", icon: HomeIcon },
  { id: "phone-bank", label: "Phone Bank", icon: Phone },
  { id: "door-knocking", label: "Door Knocking", icon: Footprints },
];

const YardSignForm = () => {
  const [form, setForm] = useState({ name: "", email: "", address: "", zip_code: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postForm("/campaign/volunteer", {
        name: form.name,
        email: form.email,
        zip_code: form.zip_code,
        interests: ["yard-sign"],
        message: `Yard sign request — deliver to: ${form.address}`,
      });
      setDone(true);
      toast.success("Yard sign request received!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-3" data-testid="yard-sign-success-message">
        <CheckCircle2 className="w-8 h-8 text-crimson shrink-0" />
        <p className="text-navy font-semibold text-base sm:text-lg">
          Your sign request is in! The campaign team will reach out about delivery.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="yard-sign-form">
      <input required type="text" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="yard-sign-name-input" className="w-full border border-navy/20 rounded-lg px-4 py-3 text-base bg-white placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
      <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="yard-sign-email-input" className="w-full border border-navy/20 rounded-lg px-4 py-3 text-base bg-white placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
      <input required type="text" placeholder="Street address for the sign" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="yard-sign-address-input" className="w-full border border-navy/20 rounded-lg px-4 py-3 text-base bg-white placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
      <div className="flex gap-3">
        <input required type="text" placeholder="ZIP" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="yard-sign-zip-input" className="w-24 border border-navy/20 rounded-lg px-4 py-3 text-base bg-white placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
        <button type="submit" disabled={busy} data-testid="yard-sign-submit-button" className="flex-1 bg-crimson hover:bg-crimson-dark disabled:opacity-60 text-white font-display font-extrabold uppercase tracking-wider text-lg rounded-lg px-5 transition-all duration-200 hover:scale-[1.02] min-h-[50px]">
          {busy ? "Sending…" : "Request My Sign"}
        </button>
      </div>
    </form>
  );
};

const VolunteerForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", zip_code: "", message: "", company: "" });
  const [interests, setInterests] = useState([]);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setInterests((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postForm("/campaign/volunteer", { ...form, interests });
      setDone(true);
      toast.success("Thank you! The campaign team will be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-xl border border-navy/10 shadow-md p-10 text-center" data-testid="volunteer-success-message">
        <CheckCircle2 className="w-14 h-14 text-crimson mx-auto mb-5" />
        <h3 className="font-display font-extrabold uppercase tracking-tight text-3xl text-navy mb-3">You're on the team!</h3>
        <p className="text-navy/70 text-base sm:text-lg max-w-md mx-auto">
          Thank you for stepping up for Delaware. A member of the campaign team will reach out shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-navy/10 shadow-md p-6 sm:p-9" data-testid="volunteer-form">
      <input type="text" name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <p className="font-mono text-xs tracking-[0.3em] uppercase text-crimson mb-4">I want to help by…</p>
      <div className="grid grid-cols-2 gap-3 mb-7">
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.id)}
            data-testid={`volunteer-option-${o.id}`}
            aria-pressed={interests.includes(o.id)}
            className={`flex flex-col items-center gap-2 rounded-lg border-2 px-3 py-4 text-center transition-all duration-150 min-h-[88px] ${
              interests.includes(o.id)
                ? "border-crimson bg-crimson/5 text-navy"
                : "border-navy/15 text-navy/60 hover:border-navy/40"
            }`}
          >
            <o.icon className={`w-6 h-6 ${interests.includes(o.id) ? "text-crimson" : "text-navy/40"}`} />
            <span className="font-display font-bold uppercase tracking-wide text-sm sm:text-base leading-tight">{o.label}</span>
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <input required type="text" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="volunteer-name-input" className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
        <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-email-input" className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
        <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="volunteer-phone-input" className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
        <input type="text" placeholder="ZIP code" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="volunteer-zip-input" className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent" />
      </div>
      <textarea placeholder="Anything you'd like the campaign to know? (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} data-testid="volunteer-message-input" className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent mb-6" />

      <button
        type="submit"
        disabled={busy}
        data-testid="volunteer-form-submit-button"
        className="w-full bg-crimson hover:bg-crimson-dark disabled:opacity-60 text-white font-display font-extrabold uppercase tracking-wider text-xl px-8 py-4 rounded-full transition-all duration-200 hover:scale-[1.02] min-h-[56px]"
      >
        {busy ? "Sending…" : "Count Me In"}
      </button>
    </form>
  );
};

const GetInvolved = () => (
  <main data-testid="get-involved-page">
    <PageHero
      kicker="Get Involved"
      lines={["This Race", "Runs on You."]}
      intro="Yard signs, house parties, phone calls, and front-porch conversations — that's how Delaware races are won. Pick a way to help and the campaign team will plug you in."
    />

    <section className="bg-cream py-16 sm:py-24 md:py-28" data-testid="volunteer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative bg-white rounded-xl border-2 border-crimson/50 shadow-md overflow-hidden mb-14" data-testid="yard-sign-card">
            <StripeBar className="h-2 w-full" />
            <div className="p-6 sm:p-9 grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-crimson mb-3 flex items-center gap-2">
                  <Signpost className="w-4 h-4" /> Request a Yard Sign
                </p>
                <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-navy leading-none mb-4">
                  Put Dave in Your Front Yard
                </h2>
                <p className="text-navy/70 text-base sm:text-lg leading-relaxed">
                  Show your support where the neighborhood can see it. Tell us where to deliver and the campaign will get a Dave Graham sign to you.
                </p>
              </div>
              <YardSignForm />
            </div>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Chapter kicker="Volunteer" title="Raise Your Hand" />
            <Reveal delay={0.15} className="mt-6 space-y-5">
              <p className="text-navy/75 text-base sm:text-lg leading-relaxed">
                Whether you have an hour a week or an hour a month, there's a job on this campaign with your name on it.
              </p>
              <ul className="space-y-3">
                {OPTIONS.map((o) => (
                  <li key={o.id} className="flex items-center gap-3 text-navy/80 text-base">
                    <o.icon className="w-5 h-5 text-crimson shrink-0" />
                    <span className="font-semibold">{o.label}</span>
                  </li>
                ))}
              </ul>
              <p className="text-navy/60 text-sm leading-relaxed">
                Your information goes directly to the campaign team and is never sold or shared.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <VolunteerForm />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-navy py-16 sm:py-24" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <Chapter kicker="On the Trail" title="Upcoming Events" dark />
          <Reveal delay={0.15} className="mt-6">
            <p className="text-cream/70 text-base sm:text-lg leading-relaxed max-w-xl">
              Forums, town halls, meet-and-greets, and parade stops — here's where to catch Dave on the trail. New dates are added as they're confirmed, and campaign subscribers always hear first.
            </p>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-5" delay={0.2}>
          <div className="space-y-5">
            {EVENTS.map((ev) => (
              <div key={ev.title} className="bg-navy-light/40 border border-cream/10 rounded-xl p-7" data-testid="event-card">
                <p className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-gold mb-3">
                  <Mic className="w-4 h-4" /> {ev.date} · {ev.time}
                </p>
                <h3 className="font-display font-extrabold uppercase tracking-wide text-2xl text-cream leading-tight mb-3">{ev.title}</h3>
                <p className="text-cream/65 text-base leading-relaxed mb-6">{ev.detail}</p>
                <a
                  href={ev.registerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="event-card-register-button"
                  className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-dark text-white font-display font-extrabold uppercase tracking-wider text-lg px-7 py-3.5 rounded-full transition-all duration-200 hover:scale-[1.03] min-h-[52px]"
                >
                  {ev.registerLabel} <ArrowUpRight className="w-5 h-5" />
                </a>
              </div>
            ))}
            <p className="text-cream/40 text-sm leading-relaxed">
              More campaign events will be announced soon — join the campaign on the homepage to be first to know.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default GetInvolved;
